import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Upload, Trash2, Edit3, Image, X, ArrowLeft, LogOut, Scale, Video,
  Calendar, Tag, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Award", "Conference", "Seminar", "Workshop", "Community", "Networking", "Advisory", "Appointment", "Other"];

const emptyForm = {
  title: "",
  short_description: "",
  full_description: "",
  event_date: "",
  event_end_date: "",
  event_type: "",
  cover_image: "",
  images: [],
  video_url: "",
  year: new Date().getFullYear(),
  published: true,
};

const CASE_SERVICES = [
  "Property Development & Conveyancing", "Notarial Services",
  "Commercial Agreements", "Civil Litigation", "Corporate Governance",
];

const emptyCaseForm = {
  title: "", practical_question: "", what_happened: "", court_decision: "",
  why_it_matters: "", actions_to_consider: "", judgment_date: "", source: "",
  related_service: "", video_url: "", cover_image: "", published: true,
};

export default function AdminGalleryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "events";
  const setActiveTab = (tab) => setSearchParams({ tab });

  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);

  // Events state
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Case Watch state
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [showCaseForm, setShowCaseForm] = useState(false);
  const [editingCaseId, setEditingCaseId] = useState(null);
  const [caseForm, setCaseForm] = useState({ ...emptyCaseForm });
  const [caseSaving, setCaseSaving] = useState(false);
  const [caseDeleteConfirm, setCaseDeleteConfirm] = useState(null);

  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Auth check
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => { setUser(res.data); setAuthChecking(false); })
      .catch(() => { navigate("/admin/login"); });
  }, [navigate]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    toast.success("Logged out");
    navigate("/admin/login");
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch { toast.error("Failed to load events"); }
    finally { setLoading(false); }
  }, []);

  const fetchCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const res = await axios.get(`${API}/case-watch`);
      setCases(res.data);
    } catch { toast.error("Failed to load cases"); }
    finally { setCasesLoading(false); }
  }, []);

  useEffect(() => { if (user) { fetchEvents(); fetchCases(); } }, [user, fetchEvents, fetchCases]);

  /* ── Upload ── */
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: true,
    });
    return `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
  };

  const handleImageUpload = async (e, isCover = false) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const newProgress = {};
    files.forEach((f) => { newProgress[f.name] = "uploading"; });
    setUploadProgress((p) => ({ ...p, ...newProgress }));

    try {
      const urls = [];
      for (const file of files) {
        const url = await uploadFile(file);
        urls.push(url);
        setUploadProgress((p) => ({ ...p, [file.name]: "done" }));
      }

      if (isCover) {
        setForm((f) => ({ ...f, cover_image: urls[0] }));
        toast.success("Cover image uploaded");
      } else {
        setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
        toast.success(`${urls.length} image(s) uploaded`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload failed — please try again");
      files.forEach((f) => {
        setUploadProgress((p) => ({ ...p, [f.name]: "error" }));
      });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress({}), 2000);
    }
  };

  const removeImage = (index) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  };

  /* ── CRUD ── */
  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (event) => {
    setEditingId(event.id);
    setForm({
      title: event.title,
      short_description: event.short_description,
      full_description: event.full_description,
      event_date: event.event_date,
      event_end_date: event.event_end_date || "",
      event_type: event.event_type,
      cover_image: event.cover_image,
      images: event.images || [],
      video_url: event.video_url || "",
      year: event.year,
      published: event.published,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.event_date || !form.event_type || !form.cover_image) {
      toast.error("Please fill in title, date, type and upload a cover image");
      return;
    }
    // Validate end date is within 5 days of start date
    if (form.event_end_date) {
      const start = new Date(form.event_date);
      const end = new Date(form.event_end_date);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);
      if (diffDays < 0) { toast.error("End date must be after start date"); return; }
      if (diffDays > 4) { toast.error("Event duration cannot exceed 5 days"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.event_date.substring(0, 4), 10),
        video_url: form.video_url || null,
        event_end_date: form.event_end_date || null,
      };
      if (editingId) {
        await axios.put(`${API}/events/${editingId}`, payload, { withCredentials: true });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, payload, { withCredentials: true });
        toast.success("Event created");
      }
      setShowForm(false);
      fetchEvents();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save event");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`, { withCredentials: true });
      toast.success("Event deleted");
      setDeleteConfirm(null);
      fetchEvents();
    } catch { toast.error("Failed to delete"); }
  };

  /* ── Case Watch CRUD ── */
  const openNewCase = () => { setEditingCaseId(null); setCaseForm({ ...emptyCaseForm }); setShowCaseForm(true); };

  const openEditCase = (c) => {
    setEditingCaseId(c.id);
    setCaseForm({
      title: c.title, practical_question: c.practical_question, what_happened: c.what_happened,
      court_decision: c.court_decision, why_it_matters: c.why_it_matters,
      actions_to_consider: c.actions_to_consider, judgment_date: c.judgment_date,
      source: c.source, related_service: c.related_service,
      video_url: c.video_url || "", cover_image: c.cover_image || "", published: c.published,
    });
    setShowCaseForm(true);
  };

  const handleSaveCase = async () => {
    if (!caseForm.title || !caseForm.practical_question || !caseForm.judgment_date || !caseForm.related_service) {
      toast.error("Please fill in title, practical question, date and service"); return;
    }
    setCaseSaving(true);
    try {
      const payload = { ...caseForm, video_url: caseForm.video_url || null, cover_image: caseForm.cover_image || null };
      if (editingCaseId) {
        await axios.put(`${API}/case-watch/${editingCaseId}`, payload, { withCredentials: true });
        toast.success("Case updated");
      } else {
        await axios.post(`${API}/case-watch`, payload, { withCredentials: true });
        toast.success("Case created");
      }
      setShowCaseForm(false);
      fetchCases();
    } catch { toast.error("Failed to save case"); }
    finally { setCaseSaving(false); }
  };

  const handleDeleteCase = async (id) => {
    try {
      await axios.delete(`${API}/case-watch/${id}`, { withCredentials: true });
      toast.success("Case deleted");
      setCaseDeleteConfirm(null);
      fetchCases();
    } catch { toast.error("Failed to delete"); }
  };

  /* ── Render ── */
  if (authChecking) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Admin Panel" description="Gallery and Case Watch management" />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-6 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-3">
                Admin Panel {user && <span className="normal-case tracking-normal text-white/40">— {user.email}</span>}
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-white tracking-tighter">
                Content Manager
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={activeTab === "cases" ? openNewCase : openNew}
                className="btn-gold px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-none flex items-center gap-2"
                data-testid="add-new-btn"
              >
                <Plus className="h-4 w-4" /> {activeTab === "cases" ? "New Case" : "New Event"}
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-white/50 hover:text-white hover:bg-white/10 rounded-none px-4 py-3"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-0 border-b border-white/10">
            <button
              onClick={() => setActiveTab("events")}
              className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                activeTab === "events" ? "text-brand-gold border-brand-gold" : "text-white/40 border-transparent hover:text-white/70"
              }`}
              data-testid="tab-events"
            >
              <Image className="h-4 w-4 inline mr-2" />Gallery Events
            </button>
            <button
              onClick={() => setActiveTab("cases")}
              className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                activeTab === "cases" ? "text-brand-gold border-brand-gold" : "text-white/40 border-transparent hover:text-white/70"
              }`}
              data-testid="tab-cases"
            >
              <Scale className="h-4 w-4 inline mr-2" />Case Watch
            </button>
          </div>
        </div>
      </section>

      {/* Events Table */}
      {activeTab === "events" && (
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-brand-gold" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <Image className="h-16 w-16 mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/50 mb-6">No events yet. Create your first gallery event.</p>
              <Button onClick={openNew} className="btn-gold rounded-none" data-testid="empty-add-event-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Event
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <motion.div
                  key={event.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-4 p-4 border border-charcoal/10 hover:border-brand-gold/30 transition-colors"
                  data-testid={`admin-event-${event.id}`}
                >
                  <img
                    src={event.cover_image}
                    alt={event.title}
                    className="w-20 h-20 object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-charcoal truncate">{event.title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-charcoal/50">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{event.event_date}{event.event_end_date ? ` — ${event.event_end_date}` : ''}</span>
                      <span className="flex items-center gap-1"><Tag className="h-3 w-3" />{event.event_type}</span>
                      <span className="flex items-center gap-1"><Image className="h-3 w-3" />{(event.images || []).length} images</span>
                      {event.published
                        ? <span className="flex items-center gap-1 text-green-600"><Eye className="h-3 w-3" />Published</span>
                        : <span className="flex items-center gap-1 text-amber-600"><EyeOff className="h-3 w-3" />Draft</span>
                      }
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/gallery/${event.id}`)}
                      className="text-charcoal/50 hover:text-brand-gold"
                      data-testid={`view-event-${event.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(event)}
                      className="text-charcoal/50 hover:text-brand-gold"
                      data-testid={`edit-event-${event.id}`}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(event.id)}
                      className="text-charcoal/50 hover:text-red-600"
                      data-testid={`delete-event-${event.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* ── Case Watch Tab ── */}
      {activeTab === "cases" && (
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {casesLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-gold" /></div>
          ) : cases.length === 0 ? (
            <div className="text-center py-20">
              <Scale className="h-16 w-16 mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/50 mb-6">No case watch entries yet.</p>
              <Button onClick={openNewCase} className="btn-gold rounded-none" data-testid="empty-add-case-btn"><Plus className="h-4 w-4 mr-2" /> Create Case</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {cases.map((c) => (
                <motion.div key={c.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-4 p-4 border border-charcoal/10 hover:border-brand-gold/30 transition-colors"
                  data-testid={`admin-case-${c.id}`}
                >
                  <Scale className="h-10 w-10 text-brand-gold/40 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-charcoal truncate">{c.title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-charcoal/50 flex-wrap">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{c.judgment_date}</span>
                      <span className="text-brand-gold text-xs">{c.related_service}</span>
                      {c.video_url && <span className="flex items-center gap-1 text-blue-500"><Video className="h-3 w-3" />Video</span>}
                      {c.published
                        ? <span className="flex items-center gap-1 text-green-600"><Eye className="h-3 w-3" />Published</span>
                        : <span className="flex items-center gap-1 text-amber-600"><EyeOff className="h-3 w-3" />Draft</span>
                      }
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/case-watch/${c.id}`)} className="text-charcoal/50 hover:text-brand-gold"><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditCase(c)} className="text-charcoal/50 hover:text-brand-gold" data-testid={`edit-case-${c.id}`}><Edit3 className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setCaseDeleteConfirm(c.id)} className="text-charcoal/50 hover:text-red-600" data-testid={`delete-case-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingId ? "Edit Event" : "New Gallery Event"}
            </DialogTitle>
            <DialogDescription className="text-charcoal/50 text-sm">
              {editingId ? "Update the event details and images below." : "Fill in the event details and upload images."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Title */}
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Woman of Stature Awards"
                className="rounded-none mt-1"
                data-testid="event-title-input"
              />
            </div>

            {/* Date, End Date & Type */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="event_date">Start Date *</Label>
                <Input
                  id="event_date"
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label htmlFor="event_end_date">End Date</Label>
                <Input
                  id="event_end_date"
                  type="date"
                  value={form.event_end_date}
                  onChange={(e) => setForm({ ...form, event_end_date: e.target.value })}
                  min={form.event_date}
                  className="rounded-none mt-1"
                  data-testid="event-end-date-input"
                />
                <p className="text-charcoal/40 text-xs mt-1">Max 5 days from start</p>
              </div>
              <div>
                <Label>Event Type *</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="event-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {EVENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Short Description */}
            <div>
              <Label htmlFor="short_desc">Short Description</Label>
              <Textarea
                id="short_desc"
                value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                placeholder="Brief summary shown on the gallery grid"
                rows={2}
                className="rounded-none mt-1"
                data-testid="event-short-desc"
              />
            </div>

            {/* Full Description */}
            <div>
              <Label htmlFor="full_desc">Full Description</Label>
              <Textarea
                id="full_desc"
                value={form.full_description}
                onChange={(e) => setForm({ ...form, full_description: e.target.value })}
                placeholder="Detailed description shown on the event detail page"
                rows={4}
                className="rounded-none mt-1"
                data-testid="event-full-desc"
              />
            </div>

            {/* Cover Image */}
            <div>
              <Label>Cover Image *</Label>
              <div className="mt-1">
                {form.cover_image ? (
                  <div className="relative inline-block">
                    <img
                      src={form.cover_image}
                      alt="Cover"
                      className="w-full max-w-xs h-48 object-cover border border-charcoal/10"
                    />
                    <button
                      onClick={() => setForm({ ...form, cover_image: "" })}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                      data-testid="remove-cover-btn"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <label
                    className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-charcoal/20 cursor-pointer hover:border-brand-gold/50 transition-colors"
                    data-testid="cover-upload-area"
                  >
                    <Upload className="h-8 w-8 text-charcoal/30 mb-2" />
                    <span className="text-sm text-charcoal/50">Click to upload cover image</span>
                    <span className="text-xs text-charcoal/30 mt-1">JPG, PNG, WebP • Max 10MB</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, true)}
                      data-testid="cover-file-input"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Gallery Images */}
            <div>
              <Label>Gallery Images</Label>
              <div className="mt-1 grid grid-cols-4 gap-3">
                {form.images.map((url, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={url}
                      alt={`Gallery ${idx + 1}`}
                      className="w-full h-24 object-cover border border-charcoal/10"
                    />
                    <button
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label
                  className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-charcoal/20 cursor-pointer hover:border-brand-gold/50 transition-colors"
                  data-testid="gallery-upload-area"
                >
                  <Plus className="h-5 w-5 text-charcoal/30" />
                  <span className="text-xs text-charcoal/40 mt-1">Add</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, false)}
                    data-testid="gallery-file-input"
                  />
                </label>
              </div>
            </div>

            {/* Upload Progress */}
            <AnimatePresence>
              {Object.entries(uploadProgress).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-1"
                >
                  {Object.entries(uploadProgress).map(([name, status]) => (
                    <div key={name} className="flex items-center gap-2 text-sm">
                      {status === "uploading" && <Loader2 className="h-3 w-3 animate-spin text-brand-gold" />}
                      {status === "done" && <CheckCircle2 className="h-3 w-3 text-green-600" />}
                      {status === "error" && <AlertCircle className="h-3 w-3 text-red-600" />}
                      <span className="truncate text-charcoal/60">{name}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Video URL */}
            <div>
              <Label htmlFor="video_url">Video URL (optional)</Label>
              <Input
                id="video_url"
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                placeholder="https://youtube.com/..."
                className="rounded-none mt-1"
                data-testid="event-video-url"
              />
            </div>

            {/* Published Toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setForm({ ...form, published: !form.published })}
                className={`relative w-11 h-6 rounded-full transition-colors ${form.published ? "bg-brand-gold" : "bg-charcoal/20"}`}
                data-testid="published-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.published ? "translate-x-5" : ""}`} />
              </button>
              <span className="text-sm text-charcoal/70">{form.published ? "Published" : "Draft"}</span>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowForm(false)}
              className="rounded-none"
              data-testid="cancel-event-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || uploading}
              className="btn-gold rounded-none px-8"
              data-testid="save-event-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingId ? "Update Event" : "Create Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Delete Event?</DialogTitle>
            <DialogDescription className="sr-only">Confirm event deletion</DialogDescription>
          </DialogHeader>
          <p className="text-charcoal/60 text-sm py-4">
            This action cannot be undone. The event and its gallery images will be permanently removed.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="rounded-none" data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700 text-white rounded-none"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Case Watch Create/Edit Dialog ── */}
      <Dialog open={showCaseForm} onOpenChange={setShowCaseForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingCaseId ? "Edit Case" : "New Case Watch"}</DialogTitle>
            <DialogDescription className="text-charcoal/50 text-sm">Upload a court judgment summary with video.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div>
              <Label>Title *</Label>
              <Input value={caseForm.title} onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })} placeholder="e.g. Can a Seller Cancel a Property Sale?" className="rounded-none mt-1" data-testid="case-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Judgment Date *</Label>
                <Input type="date" value={caseForm.judgment_date} onChange={(e) => setCaseForm({ ...caseForm, judgment_date: e.target.value })} className="rounded-none mt-1" data-testid="case-date-input" />
              </div>
              <div>
                <Label>Related Service *</Label>
                <Select value={caseForm.related_service} onValueChange={(v) => setCaseForm({ ...caseForm, related_service: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="case-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent className="bg-white">{CASE_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>The Practical Question *</Label>
              <Textarea value={caseForm.practical_question} onChange={(e) => setCaseForm({ ...caseForm, practical_question: e.target.value })} placeholder="The key question this case addresses..." rows={2} className="rounded-none mt-1" data-testid="case-question" />
            </div>
            <div>
              <Label>What Happened</Label>
              <Textarea value={caseForm.what_happened} onChange={(e) => setCaseForm({ ...caseForm, what_happened: e.target.value })} placeholder="Brief facts of the case..." rows={3} className="rounded-none mt-1" data-testid="case-happened" />
            </div>
            <div>
              <Label>What the Court Decided</Label>
              <Textarea value={caseForm.court_decision} onChange={(e) => setCaseForm({ ...caseForm, court_decision: e.target.value })} placeholder="The court's ruling..." rows={3} className="rounded-none mt-1" data-testid="case-decision" />
            </div>
            <div>
              <Label>Why It Matters</Label>
              <Textarea value={caseForm.why_it_matters} onChange={(e) => setCaseForm({ ...caseForm, why_it_matters: e.target.value })} placeholder="Practical implications..." rows={3} className="rounded-none mt-1" data-testid="case-matters" />
            </div>
            <div>
              <Label>Actions Clients Should Consider</Label>
              <Textarea value={caseForm.actions_to_consider} onChange={(e) => setCaseForm({ ...caseForm, actions_to_consider: e.target.value })} placeholder="Recommended next steps..." rows={3} className="rounded-none mt-1" data-testid="case-actions" />
            </div>
            <div>
              <Label>Source</Label>
              <Input value={caseForm.source} onChange={(e) => setCaseForm({ ...caseForm, source: e.target.value })} placeholder="e.g. Supreme Court of Appeal — Case No 123/2026" className="rounded-none mt-1" data-testid="case-source" />
            </div>
            <div>
              <Label>Video URL (YouTube/Vimeo embed link)</Label>
              <Input value={caseForm.video_url} onChange={(e) => setCaseForm({ ...caseForm, video_url: e.target.value })} placeholder="https://www.youtube.com/embed/..." className="rounded-none mt-1" data-testid="case-video-url" />
              <p className="text-charcoal/40 text-xs mt-1">Use the embed URL from YouTube "Share &gt; Embed" or Vimeo</p>
            </div>
            <div>
              <Label>Cover Image URL (optional)</Label>
              <Input value={caseForm.cover_image} onChange={(e) => setCaseForm({ ...caseForm, cover_image: e.target.value })} placeholder="https://..." className="rounded-none mt-1" data-testid="case-cover-image" />
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setCaseForm({ ...caseForm, published: !caseForm.published })} className={`relative w-11 h-6 rounded-full transition-colors ${caseForm.published ? "bg-brand-gold" : "bg-charcoal/20"}`} data-testid="case-published-toggle">
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${caseForm.published ? "translate-x-5" : ""}`} />
              </button>
              <span className="text-sm text-charcoal/70">{caseForm.published ? "Published" : "Draft"}</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowCaseForm(false)} className="rounded-none">Cancel</Button>
            <Button onClick={handleSaveCase} disabled={caseSaving} className="btn-gold rounded-none px-8" data-testid="save-case-btn">
              {caseSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingCaseId ? "Update Case" : "Create Case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Watch Delete Confirm */}
      <Dialog open={!!caseDeleteConfirm} onOpenChange={() => setCaseDeleteConfirm(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Delete Case?</DialogTitle>
            <DialogDescription className="sr-only">Confirm deletion</DialogDescription>
          </DialogHeader>
          <p className="text-charcoal/60 text-sm py-4">This action cannot be undone.</p>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setCaseDeleteConfirm(null)} className="rounded-none">Cancel</Button>
            <Button onClick={() => handleDeleteCase(caseDeleteConfirm)} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-delete-case-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
